import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { formatDateRange, resolvePublicImageUrl } from "../../../../helper/helper";
import NoDataFound from "../../../../components/common/NoDataFound";
import type { IGetAllTournaments, ITournamentsByLeagueResponse } from "../../types/home";
import "./TournamentAboutTab.scss";

interface TournamentAboutTabProps {
  tournamentId: string | null;
  leagueId?: string | null;
}

const Pill: React.FC<{ enabled: boolean; trueLabel?: string; falseLabel?: string }> = ({ enabled, trueLabel = "Enabled", falseLabel = "Disabled" }) => <span className={`about-pill ${enabled ? "about-pill--on" : "about-pill--off"}`}>{enabled ? trueLabel : falseLabel}</span>;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="about-row">
    <span className="about-row__label">{label}</span>
    <span className="about-row__value">{children ?? "—"}</span>
  </div>
);

const formatPhone = (phone?: string | null, countryCode?: string | null): string => {
  if (!phone) return "—";
  const cleaned = String(phone).trim();
  if (countryCode) {
    const cc = String(countryCode).replace(/^\+/, "");
    return `+${cc} ${cleaned}`;
  }
  return cleaned;
};

const TournamentAboutTab: React.FC<TournamentAboutTabProps> = ({ tournamentId, leagueId }) => {
  const [bannerError, setBannerError] = useState(false);
  // Reuse the same league-tournaments query already used by TournamentsDetails / TournamentRouteHydrator
  const { data, isLoading } = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, leagueId],
    enabled: !!leagueId,
    queryFn: async () => (await Api.get<ITournamentsByLeagueResponse>(`${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`)).data,
  });

  const tournament: IGetAllTournaments | undefined = data?.tournamentResponses?.find((t) => t.tournamentId === tournamentId);
  const leagueName = data?.leagueResponse?.leagueName;

  if (!tournamentId) {
    return <NoDataFound message="No tournament selected" />;
  }
  if (isLoading && !tournament) {
    return <div className="about-loading">Loading tournament details...</div>;
  }
  if (!tournament) {
    return <NoDataFound message="Tournament details not available" />;
  }

  const dates = formatDateRange(tournament.startDate ?? undefined, tournament.endDate ?? undefined);

  const tournamentBanner = resolvePublicImageUrl((tournament as IGetAllTournaments & { bannerUri?: string | null }).bannerUri);

  return (
    <div className="about-tab tournament-about-tab">
      {/* Hero */}
      <section className="about-card about-card--hero">
        <div className="about-hero-banner">
          {tournamentBanner && !bannerError ? <img className="about-hero-banner__img" src={tournamentBanner} alt={tournament.tournamentName ?? "Tournament banner"} onError={() => setBannerError(true)} /> : <div className="about-hero-banner__placeholder" aria-hidden />}
          <div className="about-hero-scrim" aria-hidden />
          <div className="about-hero-overlay">
            <div className="about-hero-meta-top">
              {tournament.tournamentCategory && <span className="about-hero-tag">{tournament.tournamentCategory}</span>}
              {leagueName && <span className="about-hero-tag about-hero-tag--ghost">{leagueName}</span>}
            </div>
            <h2 className="about-hero-title">{tournament.tournamentName ?? "—"}</h2>
            <div className="about-hero-dates">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{dates}</span>
            </div>
          </div>
        </div>
        <div className="about-hero-stats">
          <div className="about-hero-stat">
            <span className="about-hero-stat__label">Match type</span>
            <span className="about-hero-stat__value">{tournament.matchType ? tournament.matchType.replace(/_/g, " ") : "—"}</span>
          </div>
          <div className="about-hero-stat">
            <span className="about-hero-stat__label">Ball type</span>
            <span className="about-hero-stat__value">{tournament.ballType || "—"}</span>
          </div>
          <div className="about-hero-stat">
            <span className="about-hero-stat__label">Max overs</span>
            <span className="about-hero-stat__value">{tournament.maxOvers ?? "—"}</span>
          </div>
        </div>
      </section>

      <div className="about-grid">
        {/* Organizer */}
        <section className="about-card">
          <h3 className="about-card__title">Organizer</h3>
          <div className="about-card__body">
            <Row label="Name">{tournament.organizerName || "—"}</Row>
            <Row label="Email">{tournament.organizerEmailId || "—"}</Row>
            <Row label="Phone">{formatPhone(tournament.organizerPhoneNumber, tournament.organizerPhoneNumberCountryCode)}</Row>
          </div>
        </section>

        {/* Match config */}
        <section className="about-card">
          <h3 className="about-card__title">Match Configuration</h3>
          <div className="about-card__body">
            <Row label="Max overs / bowler">{tournament.maxOversPerBowler ?? "—"}</Row>
            <Row label="Max wickets">{tournament.maxWickets ?? "—"}</Row>
          </div>
        </section>

        {/* Points & rules */}
        <section className="about-card">
          <h3 className="about-card__title">Points & Rules</h3>
          <div className="about-card__body">
            <Row label="Points for win">{tournament.pointForWin ?? "—"}</Row>
            <Row label="Points for tie">{tournament.pointForTie ?? "—"}</Row>
            <Row label="Points for abandoned">{tournament.pointForAbandoned ?? "—"}</Row>
            <Row label="Bonus points">
              <Pill enabled={!!(tournament as IGetAllTournaments & { bonusPointsEnabled?: boolean }).bonusPointsEnabled} />
            </Row>
          </div>
        </section>
      </div>

      {/* Description (full width) */}
      {tournament.description && (
        <section className="about-card about-card--full">
          <h3 className="about-card__title">About</h3>
          <p className="about-description">{tournament.description}</p>
        </section>
      )}
    </div>
  );
};

export default TournamentAboutTab;
